import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SegmentedField — [F-TEXTSUBTYPE] szegmentált azonosító-mező (tajszám,
 * adószám, bankszámla, telefonszám stb.) a career apply űrlaphoz és a former
 * kérdőívhez közösen.
 *
 * Kontraktus: az érték EGYETLEN string (a szülő state-jében), a komponens a
 * szerver által adott subtype_spec szerint bontja beviteli dobozokra és
 * fűzi össze (kontrollált). A telefon transport-alakja "ISO2|körzet+szám" —
 * az E164-re alakítást a szerver (Idfield::normalize) végzi; tárolt E164
 * értéknél a hidratálás kliens-oldali leghosszabb-hívókód egyezéssel történik
 * (azonos hívókódú országoknál a lista első találata — a szerver-oldali
 * újra-normalizálás így is helyes E164-et ad).
 *
 * Ha a kapott érték nem illeszthető a spec-re (legacy adat / altípus-váltás):
 * FALLBACK — egyetlen sima input a teljes értékkel, adatvesztés nélkül.
 */
import React, { useMemo, useRef, useState } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { copyText } from '../core/clipboard';
function filterCharset(v, charset, uppercase) {
    let out = v;
    if (charset === 'num') {
        out = out.replace(/\D/g, '');
    }
    else if (charset === 'alpha') {
        out = out.replace(/[^a-zA-Z]/g, '');
    }
    if (uppercase) {
        out = out.toUpperCase();
    }
    return out;
}
/** Kanonikus (akár részleges) érték -> szegmens-értékek; null = nem illeszthető */
function splitSegments(value, spec) {
    const defs = spec.segments || [];
    const sep = spec.separator || '';
    if (value === '') {
        return defs.map(() => '');
    }
    let parts;
    if (sep !== '') {
        parts = value.split(sep);
        if (parts.length > defs.length) {
            return null;
        }
        while (parts.length < defs.length) {
            parts.push('');
        }
    }
    else {
        parts = [];
        let rest = value;
        for (let i = 0; i < defs.length; i++) {
            if (i === defs.length - 1) {
                parts.push(rest);
                rest = '';
            }
            else {
                parts.push(rest.slice(0, defs[i].len));
                rest = rest.slice(defs[i].len);
            }
        }
        if (rest !== '') {
            return null;
        }
    }
    for (let i = 0; i < parts.length; i++) {
        if (parts[i].length > defs[i].len) {
            return null;
        }
        if (parts[i] !== '' && filterCharset(parts[i], defs[i].charset, false).toUpperCase() !== parts[i].toUpperCase()) {
            return null;
        }
    }
    return parts;
}
function composeSegments(parts, spec) {
    const sep = spec.separator || '';
    const trimmed = [...parts];
    // a záró üres szegmensek levágása: részleges kitöltésnél nem keletkezik
    // "12--" alakú érték; a teljes kanonikus alak változatlan (a hidratáló a
    // hiányzó szegmenseket üresre pótolja)
    while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
        trimmed.pop();
    }
    if (trimmed.length === 0) {
        return '';
    }
    let out = trimmed.join(sep);
    if (spec.uppercase) {
        out = out.toUpperCase();
    }
    return out;
}
function parsePhone(value, countries, huAreas) {
    if (value === '') {
        return { iso2: 'HU', area: '', number: '', fallback: false };
    }
    const pipeIdx = value.indexOf('|');
    if (pipeIdx > 0) {
        const iso2 = value.slice(0, pipeIdx).toUpperCase();
        let rest = value.slice(pipeIdx + 1);
        let area = '';
        if (iso2 === 'HU') {
            let best = '';
            for (const a of huAreas) {
                if (rest.startsWith(a.code) && a.code.length > best.length) {
                    best = a.code;
                }
            }
            if (best !== '') {
                area = best;
                rest = rest.slice(best.length);
            }
        }
        return { iso2, area, number: rest, fallback: false };
    }
    if (value.startsWith('+')) {
        const digits = value.slice(1);
        // leghosszabb hívókód-egyezés; azonos hívókódnál a lista első találata
        let best = null;
        for (const c of countries) {
            if (digits.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) {
                best = c;
            }
        }
        if (!best) {
            return { iso2: 'HU', area: '', number: '', fallback: true };
        }
        let national = digits.slice(best.dial.length);
        let area = '';
        if (best.iso2 === 'HU') {
            let bestArea = '';
            for (const a of huAreas) {
                if (national.startsWith(a.code) && a.code.length > bestArea.length) {
                    bestArea = a.code;
                }
            }
            if (bestArea !== '') {
                area = bestArea;
                national = national.slice(bestArea.length);
            }
        }
        return { iso2: best.iso2, area, number: national, fallback: false };
    }
    return { iso2: 'HU', area: '', number: '', fallback: true };
}
/** szeparátorok (szóköz/NBSP/kötőjel-változatok/pont/zárójel/perjel) eltávolítása */
function stripSeparators(text) {
    return String(text).replace(/[\s\-.()/\u00A0\u202F\u2009\u2013\u2014]+/g, '');
}
/**
 * Vágólap-szöveg telefonként értelmezése: +36/0036 hívókód vagy 06-os magyar
 * nemzeti alak -> ország + HU körzet + szám. null = nem értelmezhető.
 */
function parseClipboardPhoneText(text, countries, huAreas, currentIso2) {
    const compact = stripSeparators(text);
    if (compact === '') {
        return null;
    }
    const matchDial = (digitStr) => {
        let best = null;
        for (const c of countries) {
            if (digitStr.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) {
                best = c;
            }
        }
        return best;
    };
    let iso2 = null;
    let national = '';
    if (compact.charAt(0) === '+') {
        const digits = compact.slice(1).replace(/\D/g, '');
        const m = matchDial(digits);
        if (!m) {
            return null;
        }
        iso2 = m.iso2;
        national = digits.slice(m.dial.length);
    }
    else {
        const digits = compact.replace(/\D/g, '');
        if (digits.startsWith('00') && digits.length > 8) {
            const m = matchDial(digits.slice(2));
            if (m) {
                iso2 = m.iso2;
                national = digits.slice(2 + m.dial.length);
            }
        }
        if (iso2 === null && digits.startsWith('06') && digits.length >= 9) {
            iso2 = 'HU';
            national = digits.slice(2);
        }
        if (iso2 === null) {
            iso2 = currentIso2 || 'HU';
            national = digits;
        }
    }
    if (national.length < 4) {
        return null;
    }
    let area = '';
    if (iso2 === 'HU') {
        let bestArea = '';
        for (const a of huAreas) {
            if (national.startsWith(a.code) && a.code.length > bestArea.length) {
                bestArea = a.code;
            }
        }
        if (bestArea !== '') {
            area = bestArea;
            national = national.slice(bestArea.length);
        }
    }
    return { iso2, area, number: national };
}
/** Országnév-lokalizálás Intl.DisplayNames-szel (feature detection + ISO fallback) */
function useRegionNames() {
    return useMemo(() => {
        try {
            const IntlAny = Intl;
            if (typeof IntlAny.DisplayNames !== 'undefined') {
                const lang = (typeof document !== 'undefined' && document.documentElement.lang) ||
                    (typeof navigator !== 'undefined' && navigator.language) ||
                    'hu';
                const dn = new IntlAny.DisplayNames([lang], { type: 'region' });
                return (iso2) => {
                    try {
                        return dn.of(iso2) || iso2;
                    }
                    catch (err) {
                        return iso2;
                    }
                };
            }
        }
        catch (err) {
            /* fallback lent */
        }
        return (iso2) => iso2;
    }, []);
}
export function SegmentedField({ spec, value, onChange, disabled = false, required = false, id, inputClassName = '', inputStyle, phoneCountries, phoneHuAreas, copyTitle = 'Vágólapra másol', pasteTitle = 'Beillesztés vágólapról', areaPlaceholder = 'Körzet', }) {
    const inputRefs = useRef([]);
    const [copied, setCopied] = useState(false);
    const [pasted, setPasted] = useState(false);
    const regionName = useRegionNames();
    const countries = phoneCountries || [];
    const huAreas = phoneHuAreas || [];
    const doCopy = (text) => {
        if (text === '') {
            return;
        }
        const done = () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        };
        // közös helper (Clipboard API + execCommand fallback); visszajelzés csak tényleges sikerre
        copyText(text).then((ok) => {
            if (ok) {
                done();
            }
        });
    };
    const copyButton = (text) => spec.copy && !disabled ? (_jsx("button", { type: "button", className: "segmented-copy-btn", title: copyTitle, "aria-label": copyTitle, onClick: () => doCopy(text), style: {
            // csak ikon, gomb-doboz nélkül
            border: 'none',
            background: 'transparent',
            color: copied ? '#1ab394' : '#b8b8b8',
            fontSize: 16,
            padding: '2px 4px',
            cursor: 'pointer',
            flex: '0 0 auto',
        }, children: copied ? '✓' : '⧉' })) : null;
    // Beillesztés-ikon: a vágólap tartalmát illeszti a mezőkre; csak a
    // több-dobozos (segments/phone) nézetben jelenik meg. Clipboard API
    // hiányánál / megtagadott engedélynél néma no-op.
    const pasteButton = (applyText) => spec.copy && !disabled ? (_jsx("button", { type: "button", className: "segmented-paste-btn", title: pasteTitle, "aria-label": pasteTitle, onClick: () => {
            if (typeof navigator === 'undefined' || !navigator.clipboard || !navigator.clipboard.readText) {
                return;
            }
            navigator.clipboard.readText().then((text) => {
                if (text && applyText(text)) {
                    setPasted(true);
                    setTimeout(() => setPasted(false), 1200);
                }
            }, () => undefined);
        }, style: {
            border: 'none',
            background: 'transparent',
            color: pasted ? '#1ab394' : '#b8b8b8',
            fontSize: 16,
            padding: '2px 4px',
            cursor: 'pointer',
            flex: '0 0 auto',
        }, children: pasted ? '✓' : '📋' })) : null;
    const wrapperStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
    };
    // ── FALLBACK / SINGLE ────────────────────────────────────────────────
    if (spec.kind === 'single') {
        return (_jsxs("div", { className: "segmented-field-react", style: wrapperStyle, children: [_jsx("input", { type: "text", id: id, value: value, disabled: disabled, required: required, maxLength: spec.maxlen || 64, inputMode: spec.charset === 'num' ? 'numeric' : undefined, autoComplete: "off", spellCheck: false, className: inputClassName, style: inputStyle, onChange: (e) => onChange(filterCharset(e.target.value, spec.charset, spec.uppercase)) }), copyButton(value)] }));
    }
    // ── PHONE ────────────────────────────────────────────────────────────
    if (spec.kind === 'phone') {
        const parts = parsePhone(value, countries, huAreas);
        if (parts.fallback) {
            return (_jsxs("div", { className: "segmented-field-react", style: wrapperStyle, children: [_jsx("input", { type: "text", id: id, value: value, disabled: disabled, required: required, maxLength: 32, autoComplete: "off", spellCheck: false, className: inputClassName, style: inputStyle, onChange: (e) => onChange(e.target.value) }), copyButton(value)] }));
        }
        const composePhone = (iso2, area, num) => num === '' ? '' : `${iso2}|${area}${num}`;
        const countryOptions = countries.map((c) => ({
            value: c.iso2,
            label: `${regionName(c.iso2)} (+${c.dial})`,
        }));
        const areaOptions = huAreas.map((a) => ({
            value: a.code,
            label: `${a.code} - ${a.label}`,
        }));
        return (_jsxs("div", { className: "segmented-field-react", style: wrapperStyle, children: [_jsx(SearchableSelect, { options: countryOptions, value: parts.iso2, disabled: disabled, placeholder: "HU (+36)", selectedLabelOf: (o) => {
                        const c = countries.find((cc) => cc.iso2 === o.value);
                        return c ? '+' + c.dial : o.value;
                    }, onChange: (iso2) => {
                        const nextArea = iso2 === 'HU' ? parts.area : '';
                        onChange(composePhone(iso2, nextArea, parts.number));
                    } }), parts.iso2 === 'HU' && huAreas.length > 0 && (_jsx(SearchableSelect, { options: areaOptions, value: parts.area, disabled: disabled, placeholder: areaPlaceholder, selectedLabelOf: (o) => o.value, onChange: (code) => onChange(composePhone(parts.iso2, code, parts.number)) })), _jsx("input", { type: "text", id: id, value: parts.number, disabled: disabled, required: required, maxLength: 15, inputMode: "tel", autoComplete: "off", spellCheck: false, className: inputClassName, style: { ...(inputStyle || {}), width: 140, flex: '0 0 auto' }, onChange: (e) => onChange(composePhone(parts.iso2, parts.area, e.target.value.replace(/\D/g, ''))) }), copyButton(value), pasteButton((text) => {
                    const parsed = parseClipboardPhoneText(text, countries, huAreas, parts.iso2);
                    if (!parsed) {
                        return false;
                    }
                    onChange(composePhone(parsed.iso2, parsed.area, parsed.number));
                    return true;
                })] }));
    }
    // ── SEGMENTS ─────────────────────────────────────────────────────────
    const defs = spec.segments || [];
    const parts = splitSegments(value, spec);
    if (parts === null) {
        // legacy / nem illeszthető érték: sima input, adatvesztés nélkül
        return (_jsxs("div", { className: "segmented-field-react", style: wrapperStyle, children: [_jsx("input", { type: "text", id: id, value: value, disabled: disabled, required: required, maxLength: 64, autoComplete: "off", spellCheck: false, className: inputClassName, style: inputStyle, onChange: (e) => onChange(e.target.value) }), copyButton(value)] }));
    }
    const updatePart = (index, raw) => {
        const filtered = filterCharset(raw, defs[index].charset, spec.uppercase).slice(0, defs[index].len);
        const next = [...parts];
        next[index] = filtered;
        onChange(composeSegments(next, spec));
        if (filtered.length >= defs[index].len && index < defs.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handleKeyDown = (index, e) => {
        const el = e.currentTarget;
        if (e.key === 'Backspace' && el.value === '' && index > 0) {
            inputRefs.current[index - 1]?.focus();
            e.preventDefault();
        }
        else if (e.key === 'ArrowLeft' && (el.selectionStart || 0) === 0 && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
        else if (e.key === 'ArrowRight' &&
            (el.selectionStart || 0) >= el.value.length &&
            index < defs.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };
    const handlePaste = (index, e) => {
        const text = e.clipboardData.getData('text');
        if (!text) {
            return;
        }
        e.preventDefault();
        const cleaned = text.replace(/[\s\-.()/   –—]+/g, '');
        const next = [...parts];
        let pos = 0;
        let lastIdx = index;
        for (let i = index; i < defs.length; i++) {
            const chunk = cleaned.substr(pos, defs[i].len);
            next[i] = filterCharset(chunk, defs[i].charset, spec.uppercase);
            if (chunk !== '') {
                lastIdx = i;
            }
            pos += defs[i].len;
        }
        onChange(composeSegments(next, spec));
        inputRefs.current[lastIdx]?.focus();
    };
    return (_jsxs("div", { className: "segmented-field-react", style: wrapperStyle, children: [defs.map((def, i) => (_jsxs(React.Fragment, { children: [i > 0 && (spec.separator || '') !== '' && (_jsx("span", { "aria-hidden": "true", style: { color: '#999', fontWeight: 600 }, children: spec.separator })), _jsx("input", { ref: (el) => (inputRefs.current[i] = el), type: "text", id: i === 0 ? id : undefined, value: parts[i], disabled: disabled, required: required && !def.optional && i === 0, maxLength: def.len, inputMode: def.charset === 'num' ? 'numeric' : undefined, autoComplete: "off", spellCheck: false, className: inputClassName, style: {
                            ...(inputStyle || {}),
                            width: Math.max(44, def.len * 13 + 28),
                            textAlign: 'center',
                            flex: '0 0 auto',
                            paddingLeft: 4,
                            paddingRight: 4,
                        }, "aria-label": `${i + 1}. rész`, onChange: (e) => updatePart(i, e.target.value), onKeyDown: (e) => handleKeyDown(i, e), onPaste: (e) => handlePaste(i, e) })] }, i))), copyButton(value), pasteButton((text) => {
                const cleaned = stripSeparators(text);
                if (cleaned === '') {
                    return false;
                }
                const next = [];
                let pos = 0;
                for (const def of defs) {
                    next.push(filterCharset(cleaned.substr(pos, def.len), def.charset, spec.uppercase));
                    pos += def.len;
                }
                onChange(composeSegments(next, spec));
                return true;
            })] }));
}
export default SegmentedField;
